/**
 * Recorded debriefs (DEBRIEF-RECORDING): the edge appliance's cameras record
 * the video, this laptop the audio; the server puts them together as a
 * coaching recording of the session.
 */
import { apiClient } from './client';

export interface DebriefCamera {
  id: string;
  name: string;
}

export interface DebriefAppliance {
  id: string;
  label: string;
  status: string;
  last_seen_at: string | null;
  cameras: DebriefCamera[];
}

export type DebriefStatus = 'recording' | 'assembling' | 'ready' | 'failed';

export interface Debrief {
  id: string;
  status: DebriefStatus;
  started_at: string;
  ended_at: string | null;
  stop_reason: string | null;
  camera_ids: string[];
  error: string | null;
  result_attachment_ids: string[] | null;
  max_seconds: number;
}

export interface DebriefSetup {
  appliances: DebriefAppliance[];
  default_appliance_id: string | null;
  default_camera_ids: string[];
  debriefs: Debrief[];
}

const t = (tenantId: string) => `/api/v1/tenants/${tenantId}`;

export async function getDebriefSetup(tenantId: string, sessionRowId: string): Promise<DebriefSetup> {
  const { data } = await apiClient.get<DebriefSetup>(`${t(tenantId)}/sessions/${sessionRowId}/debriefs`);
  return data;
}

export async function startDebrief(
  tenantId: string,
  sessionRowId: string,
  body: { appliance_id: string; camera_ids: string[] },
): Promise<Debrief & { server_now: string }> {
  const { data } = await apiClient.post<Debrief & { server_now: string }>(
    `${t(tenantId)}/sessions/${sessionRowId}/debriefs`,
    body,
  );
  return data;
}

/** Still recording? `false` = stop (the time limit, or stopped elsewhere). */
export async function debriefHeartbeat(tenantId: string, debriefId: string): Promise<boolean> {
  const { data } = await apiClient.post<{ recording: boolean }>(
    `${t(tenantId)}/debriefs/${debriefId}/heartbeat`,
  );
  return data.recording;
}

export async function putDebriefAudio(
  tenantId: string,
  debriefId: string,
  seq: number,
  chunk: Blob,
  mime: string,
): Promise<void> {
  await apiClient.put(`${t(tenantId)}/debriefs/${debriefId}/audio/${seq}`, chunk, {
    headers: { 'Content-Type': mime },
  });
}

export async function stopDebrief(
  tenantId: string,
  debriefId: string,
  body: { audio_offset_s: number | null; audio_parts: number; audio_mime: string | null },
): Promise<Debrief> {
  const { data } = await apiClient.post<Debrief>(`${t(tenantId)}/debriefs/${debriefId}/stop`, body);
  return data;
}
